// client/src/coms/organisation/libraryParts.js
// Building blocks shared by the library browsers: item rows/tiles with an accessible actions menu,
// breadcrumbs and source status labels.
import React, { useEffect, useRef, useState } from 'react';
import { ProviderBadge } from './orgShared';

export function statusLabel(s) {
  return (
    {
      ok: 'Connected',
      auth_error: 'Needs reconnecting',
      access_denied: 'Access denied by provider',
      rate_limited: 'Rate limited',
      unavailable: 'Provider unavailable',
      disconnected: 'Disconnected',
    }[s] || s
  );
}

export function Breadcrumbs({ crumbs, onGo }) {
  return (
    <nav className="vxo-crumbs" aria-label="Folder path">
      {crumbs.map((c, i) => (
        <span key={`${c.id}-${i}`} className="vxo-crumb">
          {i > 0 ? <span aria-hidden="true">›</span> : null}
          {i === crumbs.length - 1 ? (
            <span aria-current="page">{c.name}</span>
          ) : (
            <button type="button" className="vxo-linkbtn" onClick={() => onGo(c.id, i)}>
              {c.name}
            </button>
          )}
        </span>
      ))}
    </nav>
  );
}

// One file/folder row (list) or tile (grid) with an accessible actions menu.
export function ItemRow({ view = 'list', icon, provider, name, meta, thumb, onOpen, actions = [], badge, testId }) {
  const [open, setOpen] = useState(false);
  const btn = useRef(null);
  const menu = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const list = actions.filter(Boolean);
  useEffect(() => {
    if (!open) return undefined;
    const r = btn.current.getBoundingClientRect();
    setPos({ top: Math.min(window.innerHeight - 40, r.bottom + 4), left: Math.max(8, Math.min(window.innerWidth - 240, r.right - 230)) });
    const t = setTimeout(() => {
      const first = menu.current && menu.current.querySelector('[role="menuitem"]');
      if (first) first.focus();
    }, 0);
    const close = (e) => {
      if (menu.current && menu.current.contains(e.target)) return;
      if (btn.current && btn.current.contains(e.target)) return;
      setOpen(false);
    };
    const key = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        btn.current && btn.current.focus();
      }
      if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && menu.current && menu.current.contains(document.activeElement)) {
        e.preventDefault();
        const els = Array.from(menu.current.querySelectorAll('[role="menuitem"]'));
        const i = els.indexOf(document.activeElement);
        const next = els[(i + (e.key === 'ArrowDown' ? 1 : -1) + els.length) % els.length];
        if (next) next.focus();
      }
    };
    document.addEventListener('mousedown', close, true);
    document.addEventListener('keydown', key, true);
    return () => {
      clearTimeout(t);
      document.removeEventListener('mousedown', close, true);
      document.removeEventListener('keydown', key, true);
    };
  }, [open]);
  return (
    <div
      className={`vxo-item is-${view}`}
      role="listitem"
      onContextMenu={(e) => {
        if (!list.length) return;
        e.preventDefault();
        setOpen(true);
      }}
      data-testid={testId}
    >
      <button type="button" className="vxo-item-main" onClick={onOpen} title={name}>
        {thumb ? <img className="vxo-thumb" src={thumb} alt="" loading="lazy" /> : <span className="vxo-item-icon" aria-hidden="true">{icon}</span>}
        <span className="vxo-item-text">
          <span className="vxo-item-name">
            {provider ? <ProviderBadge provider={provider} /> : null}
            <span className="vxo-item-name-text">{name}</span>
            {badge ? <span className="vxo-badge">{badge}</span> : null}
          </span>
          {meta ? <span className="vxo-item-meta">{meta}</span> : null}
        </span>
      </button>
      {list.length ? (
        <button ref={btn} type="button" className="vx-icon-btn vxo-item-more" aria-haspopup="menu" aria-expanded={open} aria-label={`Actions for ${name}`} onClick={() => setOpen((v) => !v)}>
          ⋯
        </button>
      ) : null}
      {open ? (
        <div ref={menu} className="vx-menu vx-pz" role="menu" style={{ top: pos.top, left: pos.left }}>
          {list.map((a) =>
            a.href ? (
              <a key={a.label} role="menuitem" className="vx-menu-item" href={a.href} target={a.external ? '_blank' : undefined} rel="noopener noreferrer" onClick={() => setOpen(false)}>
                {a.label}
              </a>
            ) : (
              <button
                key={a.label}
                type="button"
                role="menuitem"
                className={`vx-menu-item${a.danger ? ' is-danger' : ''}`}
                disabled={a.disabled}
                onClick={() => {
                  setOpen(false);
                  a.onClick();
                }}
              >
                {a.label}
              </button>
            )
          )}
        </div>
      ) : null}
    </div>
  );
}
